/**
 * RequestCallModal Component
 * Feature 102: Request a call
 *
 * Modal dialog for care seekers to request a phone call from a service
 * provider. Replaces the old consultation-request form as the primary
 * contact action (The Key intake-form providers excluded).
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { formatUSPhone } from '@/shared/utils/formatters';
import { consultationsKeys } from '../hooks/useConsultations';
import type { ConsultationRequest } from '../types';

const PHONE_PATTERN = /^\(\d{3}\) \d{3}-\d{4}$/;
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

type RequestCallModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ServiceProvider entity id (not the provider's User id). */
  providerId: string;
  providerName: string;
  profileViewEngagementId?: string;
  /** "TYPE:ID" referral marker from the profile URL (?referral=...) — links
   *  the call request back to the originating referral. */
  referralMarker?: string | null;
};

const REFERRAL_MARKER_TYPES = [
  'PROVIDER_REFERRAL',
  'SP_PROVIDER_REFERRAL',
  'MANUAL_PROFESSIONAL_REFERRAL',
];

const parseReferralMarker = (
  marker: string | null | undefined,
): { referralSourceType: string; referralSourceId: string } | null => {
  if (!marker) return null;
  const [type, id] = marker.split(':');
  if (!REFERRAL_MARKER_TYPES.includes(type) || !id) return null;
  return { referralSourceType: type, referralSourceId: id };
};

export function RequestCallModal({
  open,
  onOpenChange,
  providerId,
  providerName,
  profileViewEngagementId,
  referralMarker,
}: RequestCallModalProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset form when the modal opens; email prepopulates from the account.
  useEffect(() => {
    if (open) {
      setPhone('');
      setEmail(user?.email ?? '');
      setError('');
      setIsSuccess(false);
    }
  }, [open, user?.email]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<ConsultationRequest>(
        '/consultations/call-request',
        {
          providerId,
          phone,
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(profileViewEngagementId ? { profileViewEngagementId } : {}),
          ...(parseReferralMarker(referralMarker) ?? {}),
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: consultationsKeys.all });
    },
  });

  const isPhoneValid = PHONE_PATTERN.test(phone);
  // Email is optional (server defaults to the account email) but must look
  // like an email when provided.
  const isEmailValid = !email.trim() || EMAIL_PATTERN.test(email.trim());
  const isSubmitting = createMutation.isPending;
  const canSubmit = isPhoneValid && isEmailValid && !isSubmitting && !isSuccess;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');

    try {
      await createMutation.mutateAsync();
      // The success state stays until the user closes it — no auto-close.
      setIsSuccess(true);
    } catch (err: unknown) {
      const errorMessage =
        (err as { message?: string }).message ||
        'Failed to send your request. Please try again.';
      setError(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            Request a call from {providerName}
          </DialogTitle>
          <DialogDescription>
            We'll share your phone and email with {providerName} so they can
            reach you — this is the only time your contact info is shared.
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div>
            <div className="py-4">
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  {providerName} will call you soon
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="bg-[#026857] hover:bg-[#025247]"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="call-request-phone">
                Phone number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="call-request-phone"
                type="tel"
                placeholder="(555) 555-5555"
                value={phone}
                onChange={(e) => setPhone(formatUSPhone(e.target.value))}
                disabled={isSubmitting || isSuccess}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="call-request-email">Email</Label>
              <Input
                id="call-request-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting || isSuccess}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-[#026857] hover:bg-[#025247]"
            >
              {isSubmitting ? 'Sending...' : 'Request a call'}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
